import type { Schemas } from "@softmax/observatory-client/types";
import type { DivisionLeaderboardRead } from "../data/play-api";
import type { BoardRow, PageShell, ProgramRow } from "./types";
export const playName = /·play\d*$/;
export const boardLimit = 10;
type BoardEntry = Extract<
  DivisionLeaderboardRead,
  {
    readonly status: "published";
  }
>["summary"]["entries"][number];
type Membership = Schemas["LeaguePolicyMembershipPublic"];
export function boardForViewer({
  shell,
  leaderboard,
}: {
  readonly shell: PageShell;
  readonly leaderboard: DivisionLeaderboardRead | null;
}) {
  const entries =
    leaderboard?.status === "published" ? leaderboard.summary.entries : [];
  // A ranked player remains present here even when their seat read is behind the leaderboard.
  const playerId = shell.seat?.player_id ?? shell.viewer.playerId;
  const mineEntry = playerId
    ? (entries.find((entry) => entry.player_id === playerId) ?? null)
    : null;
  const rowOf = (entry: BoardEntry): BoardRow => ({
    rank: entry.rank,
    cogent: entry.player_name?.replace(playName, "") ?? "unnamed",
    owner: entry.owner_name ?? null,
    program: entry.policy_label ?? null,
    score: entry.score,
    mine: entry === mineEntry,
  });
  return {
    entries,
    playerId,
    mineEntry,
    state: {
      boardStatus: leaderboard?.status ?? "unpublished",
      board: entries.slice(0, boardLimit).map(rowOf),
      mineBelow:
        mineEntry && mineEntry.rank > boardLimit ? rowOf(mineEntry) : null,
    },
  };
}
type ViewerBoard = ReturnType<typeof boardForViewer>;
function programStatus(seat: Membership): ProgramRow["status"] {
  if (seat.status === "disqualified")
    return seat.substatus === "inactive" ? "retired" : "disqualified";

  if (seat.is_champion) return "playing";

  return seat.status === "competing" ? "benched" : "released";
}
export function programsForViewer({
  memberships,
  board,
}: {
  readonly memberships: readonly Membership[];
  readonly board: ViewerBoard;
}) {
  // mine=true includes every player in the account; all displayed programs belong to the selected player.
  const seats = memberships.filter(
    (seat) =>
      !!board.playerId && seat.policy_version.player_id === board.playerId,
  );
  const sorted = seats
    .map((seat) => {
      const out = seat.status === "disqualified";
      const champion = seat.is_champion && !out;
      return {
        id: seat.policy_version.id,
        label: seat.policy_version.label,
        rank: champion ? (board.mineEntry?.rank ?? null) : null,
        champion,
        out,
        status: programStatus(seat),
        version: seat.policy_version.version ?? 0,
        submittedAt: seat.created_at ?? null,
      };
    })
    .toSorted(
      (a, b) =>
        Number(b.champion) - Number(a.champion) ||
        Number(b.out) - Number(a.out) ||
        b.version - a.version,
    );
  const yours: ProgramRow[] = sorted.map((program) => ({
    label: program.label,
    rank: program.rank,
    of: board.entries.length || null,
    champion: program.champion,
    status: program.status,
    id: program.id,
    score: program.champion ? (board.mineEntry?.score ?? null) : null,
    submittedAt: program.submittedAt,
  }));

  // An absent membership can lag a ranked champion. A loaded retired seat remains authoritative.
  if (board.mineEntry && seats.length === 0) {
    yours.unshift({
      label: board.mineEntry.policy_label ?? "your program",
      rank: board.mineEntry.rank,
      of: board.entries.length || null,
      champion: true,
      status: "playing",
      id: "",
      score: board.mineEntry.score,
      submittedAt: null,
    });
  }

  return { yours };
}
